// src/screens/admin/VuelosPegarActualizacion.jsx
// Sub-sección A del panel "Vuelos Guyana": pegar el mensaje de un proveedor,
// procesarlo con IA (netlify/functions/procesar-vuelos.js) y revisar/editar
// cada ruta detectada en una tabla editable ANTES de publicarla. Nada se
// guarda en Supabase hasta que Yoedis toca "Publicar".
import React, { useState, useEffect } from 'react';
import { toast } from '../../components/UI';
import {
  procesarMensajeVuelos, buscarRutaDuplicada, publicarRutaVuelo, getRutasExistentesParaCiudadDestino,
  calcPrecioZelle, calcPrecioInfante, DESTINO_CATEGORIAS, guessDestinoCategoria, canonicalizarCiudad,
} from '../../services/vuelosGuyanaService';

let _rowId = 0;
const nextRowId = () => `row_${++_rowId}`;

function filaDesdeRuta(raw, proveedor) {
  const destino_categoria = guessDestinoCategoria(raw.ciudad_salida, raw.destino);
  return {
    _id: nextRowId(),
    ciudad_salida: canonicalizarCiudad(raw.ciudad_salida, proveedor),
    destino: (raw.destino || '').trim(),
    destino_categoria,
    aerolinea: (raw.aerolinea || '').trim(),
    escala: (raw.escala || '').trim(),
    frecuencia: (raw.frecuencia || '').trim(),
    precio_base_usd: raw.precio_base_usd ?? '',
    precio_zelle: raw.precio_base_usd != null ? calcPrecioZelle(raw.precio_base_usd, proveedor.margen_usd_default) : '',
    precio_nino: '',
    // Solo si el mensaje trae un segundo precio ("Ida y vuelta desde X / Solo ida desde Y"): precio_base_usd
    // es el de solo ida y este es el de ida y vuelta, con el mismo margen. Vacío = no aplica.
    precio_base_ida_vuelta: raw.precio_base_ida_vuelta ?? '',
    precio_zelle_ida_vuelta: raw.precio_base_ida_vuelta != null ? calcPrecioZelle(raw.precio_base_ida_vuelta, proveedor.margen_usd_default) : '',
    // El precio de infante solo aplica a Guyana -- otros destinos (México,
    // Miami→Cuba, Rep. Dominicana, San Cristóbal, Suriname) no lo cobran,
    // aunque el proveedor tenga infante_precio_base configurado (un mismo
    // proveedor puede publicar rutas a varios destinos).
    precio_infante: destino_categoria === 'guyana' ? (calcPrecioInfante(proveedor) ?? '') : '',
    fechas: (raw.fechas || []).map((f) => ({ fecha: f.fecha || '', cupos: f.cupos ?? '' })),
    // undefined = todavía no se resolvió contra lo que ya existe en la base
    // (lo resuelve el efecto de abajo apenas carga las rutas existentes).
    // '' = decidido explícitamente que es una ruta nueva, no reemplaza nada.
    // uuid = reemplaza esa ruta existente puntual.
    reemplazarRutaId: undefined,
  };
}

function FilaEditable({ fila, proveedor, proveedorId, onChange, onRemove }) {
  const [existentes, setExistentes] = useState([]);
  const [cargandoExistentes, setCargandoExistentes] = useState(false);

  useEffect(() => {
    let cancelado = false;
    setCargandoExistentes(true);
    getRutasExistentesParaCiudadDestino(proveedorId, fila.ciudad_salida, fila.destino).then((rutas) => {
      if (cancelado) return;
      setExistentes(rutas);
      setCargandoExistentes(false);
      // Solo auto-selecciona la primera vez que se resuelve esta fila --
      // si Yoedis ya eligió algo a mano, no se le pisa la elección.
      if (fila.reemplazarRutaId === undefined) {
        buscarRutaDuplicada(proveedorId, fila.ciudad_salida, fila.destino, fila.aerolinea, fila.escala).then((match) => {
          if (!cancelado) onChange({ ...fila, reemplazarRutaId: match ? match.id : '' });
        });
      }
    });
    return () => { cancelado = true; };
  }, [proveedorId, fila.ciudad_salida, fila.destino]);

  const set = (k) => (e) => onChange({ ...fila, [k]: e.target.value });
  const setFecha = (i, k) => (e) => {
    const fechas = fila.fechas.map((f, idx) => (idx === i ? { ...f, [k]: e.target.value } : f));
    onChange({ ...fila, fechas });
  };
  const addFecha = () => onChange({ ...fila, fechas: [...fila.fechas, { fecha: '', cupos: '' }] });
  const removeFecha = (i) => onChange({ ...fila, fechas: fila.fechas.filter((_, idx) => idx !== i) });

  return (
    <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: 16, marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <p style={{ fontSize: 12, color: 'var(--orange)', fontWeight: 700, letterSpacing: 0.5 }}>RUTA DETECTADA</p>
        <button onClick={onRemove} style={{ background: 'none', color: 'var(--error)', fontSize: 12, fontWeight: 700 }}>✕ Quitar</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
        <div><label>Ciudad salida</label><input value={fila.ciudad_salida} onChange={set('ciudad_salida')} /></div>
        <div><label>Destino</label><input value={fila.destino} onChange={set('destino')} /></div>
      </div>
      <div style={{ marginBottom: 8 }}>
        <label>Categoría de destino</label>
        <select value={fila.destino_categoria} onChange={set('destino_categoria')} style={{
          background: 'var(--navy-card)', border: '1px solid var(--navy-border)', color: 'var(--white)',
          borderRadius: 10, padding: '13px 16px', width: '100%', fontSize: 14,
        }}>
          {DESTINO_CATEGORIAS.map((c) => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
        </select>
      </div>
      <div style={{ marginBottom: 8 }}><label>Aerolínea (opcional)</label><input value={fila.aerolinea} onChange={set('aerolinea')} placeholder="El mensaje no siempre la nombra -- puedes dejarla vacía" /></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
        <div><label>Escala</label><input value={fila.escala} onChange={set('escala')} placeholder="Directo si está vacío" /></div>
        <div><label>Frecuencia</label><input value={fila.frecuencia} onChange={set('frecuencia')} /></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: proveedor.categorias === 'adulto_nino_infante' ? '1fr 1fr 1fr 1fr' : '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
        <div><label>Precio base USD</label><input type="number" value={fila.precio_base_usd} onChange={set('precio_base_usd')} inputMode="decimal" /></div>
        <div><label>Precio Zelle</label><input type="number" value={fila.precio_zelle} onChange={set('precio_zelle')} inputMode="decimal" /></div>
        {proveedor.categorias === 'adulto_nino_infante' && (
          <div><label>Precio niño</label><input type="number" value={fila.precio_nino} onChange={set('precio_nino')} inputMode="decimal" /></div>
        )}
        <div><label>Precio infante</label><input type="number" value={fila.precio_infante} onChange={set('precio_infante')} inputMode="decimal" /></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
        <div><label>Costo ida y vuelta (opcional)</label><input type="number" value={fila.precio_base_ida_vuelta} onChange={set('precio_base_ida_vuelta')} inputMode="decimal" placeholder="Solo si el mensaje lo trae" /></div>
        <div><label>Zelle ida y vuelta</label><input type="number" value={fila.precio_zelle_ida_vuelta} onChange={set('precio_zelle_ida_vuelta')} inputMode="decimal" /></div>
      </div>

      {(cargandoExistentes || existentes.length > 0) && (
        <div style={{ marginBottom: 12, padding: 10, background: 'rgba(255,107,0,0.06)', border: '1px solid rgba(255,107,0,0.25)', borderRadius: 10 }}>
          <label style={{ color: 'var(--orange)' }}>
            {cargandoExistentes ? 'Revisando qué ya existe para esta ciudad → destino...' : `Ya existen ${existentes.length} ruta(s) para ${fila.ciudad_salida} → ${fila.destino}:`}
          </label>
          {!cargandoExistentes && (
            <select
              value={fila.reemplazarRutaId ?? ''}
              onChange={(e) => onChange({ ...fila, reemplazarRutaId: e.target.value })}
              style={{
                background: 'var(--navy-card)', border: '1px solid var(--navy-border)', color: 'var(--white)',
                borderRadius: 10, padding: '10px 12px', width: '100%', fontSize: 13, marginTop: 6,
              }}
            >
              <option value="">➕ Es una ruta nueva, no reemplaza ninguna</option>
              {existentes.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.activa ? '🔁' : '⚠️ (inactiva) 🔁'} Reemplaza: {r.aerolinea || 'sin aerolínea'}{r.escala ? ` · ${r.escala}` : ''}{r.frecuencia ? ` · ${r.frecuencia}` : ''} · ${r.precio_zelle}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      <p style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 700, letterSpacing: 0.5, marginBottom: 8 }}>FECHAS Y CUPOS</p>
      {fila.fechas.map((f, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
          <input type="date" value={f.fecha} onChange={setFecha(i, 'fecha')} style={{ flex: 2 }} />
          <input type="number" value={f.cupos} onChange={setFecha(i, 'cupos')} placeholder="Cupos" inputMode="numeric" style={{ flex: 1 }} />
          <button onClick={() => removeFecha(i)} style={{ background: 'none', color: 'var(--error)', fontSize: 16, padding: '0 6px' }}>✕</button>
        </div>
      ))}
      <button onClick={addFecha} style={{ background: 'var(--navy-card)', border: '1px dashed var(--navy-border)', color: 'var(--muted)', borderRadius: 8, padding: '8px 0', width: '100%', fontSize: 12, marginTop: 4 }}>+ Agregar fecha</button>
    </div>
  );
}

export default function VuelosPegarActualizacion({ proveedores }) {
  const [proveedorId, setProveedorId] = useState('');
  const [texto, setTexto] = useState('');
  const [procesando, setProcesando] = useState(false);
  const [filas, setFilas] = useState([]);
  const [publicando, setPublicando] = useState(false);

  useEffect(() => {
    if (!proveedorId && proveedores.length > 0) setProveedorId(proveedores[0].id);
  }, [proveedores, proveedorId]);

  const proveedor = proveedores.find((p) => p.id === proveedorId);

  const handleProcesar = async () => {
    if (!proveedorId) return toast('Selecciona un proveedor.', 'error');
    if (!texto.trim()) return toast('Pega el mensaje del proveedor.', 'error');
    setProcesando(true);
    try {
      const rutas = await procesarMensajeVuelos(proveedorId, texto);
      if (rutas.length === 0) {
        toast('No se detectaron rutas en el mensaje.', 'error');
      } else {
        setFilas(rutas.map((r) => filaDesdeRuta(r, proveedor)));
        toast(`${rutas.length} ruta(s) detectada(s) ✅`);
      }
    } catch {
      toast('Error al procesar el mensaje.', 'error');
    }
    setProcesando(false);
  };

  const updateFila = (id, nueva) => setFilas((fs) => fs.map((f) => (f._id === id ? nueva : f)));
  const removeFila = (id) => setFilas((fs) => fs.filter((f) => f._id !== id));

  // Mientras una fila todavía no resolvió su comparación contra las rutas
  // existentes (reemplazarRutaId === undefined), no se deja publicar --
  // así nunca se cuela una fila publicada como "nueva" solo porque se hizo
  // clic en Publicar antes de que terminara de cargar.
  const hayFilasSinResolver = filas.some((f) => f.reemplazarRutaId === undefined);

  // Reemplazar una ruta existente con una fila sin fechas (ej. el proveedor
  // solo dio "todos los miércoles a partir de..." sin listar fechas puntuales
  // -- procesar-vuelos.js las deja vacías a propósito, nunca las inventa)
  // borraría TODAS las fechas ya cargadas de esa ruta sin poner ninguna
  // nueva en su lugar. Un solo aviso antes de publicar todo el lote, no uno
  // por fila (para no volver al window.confirm en medio del lote que ya se
  // sacó a propósito).
  const filasReemplazoSinFechas = (filas) =>
    filas.filter((f) => f.reemplazarRutaId && f.fechas.length === 0);

  const handlePublicar = async () => {
    if (filas.length === 0 || hayFilasSinResolver) return;
    const riesgosas = filasReemplazoSinFechas(filas);
    if (riesgosas.length > 0) {
      const lista = riesgosas.map((f) => `${f.ciudad_salida} → ${f.destino}`).join(', ');
      if (!window.confirm(
        `${lista}: esta(s) ruta(s) va(n) a reemplazar una ya publicada pero no traen ninguna fecha nueva -- eso borraría TODAS las fechas que ya tenías cargadas para esa ruta, sin poner ninguna en su lugar.\n\n¿Seguro que quieres continuar? Si el proveedor solo dio la frecuencia sin fechas puntuales, mejor toca "✕ Quitar" en esa tarjeta y agrega la fecha nueva desde Rutas Publicadas.`
      )) return;
    }
    setPublicando(true);
    let publicadas = 0;
    try {
      for (const filaOriginal of filas) {
        // Re-normaliza justo antes de publicar por si Yoedis editó el campo a
        // mano después de procesar el mensaje (ej. escribió "Holguin" sin
        // acento) -- así la detección de duplicados de abajo siempre compara
        // contra la misma grafía que ya existe en la base.
        const fila = {
          ...filaOriginal,
          ciudad_salida: canonicalizarCiudad(filaOriginal.ciudad_salida, proveedor),
          aerolinea: (filaOriginal.aerolinea || '').trim(),
        };

        // aerolinea es opcional -- varios bloques de rutas (ej. México, Miami)
        // no la nombran en el texto y son rutas reales igual, con precio y
        // fechas confirmados. Solo ciudad/destino/precio son obligatorios.
        if (!fila.ciudad_salida || !fila.destino || !fila.precio_zelle) {
          toast(`Falta completar campos obligatorios en la ruta ${fila.ciudad_salida || '?'} → ${fila.destino || '?'}.`, 'error');
          continue;
        }

        // La decisión de si esta fila reemplaza una ruta ya existente (o es
        // nueva) se toma ANTES de publicar, visible en cada tarjeta -- ver
        // el selector "Ya existen N ruta(s)..." en FilaEditable. Ya no se
        // adivina ni se pregunta con un window.confirm en medio del lote.
        const reemplazarId = filaOriginal.reemplazarRutaId || null;

        await publicarRutaVuelo({ ...fila, proveedor_id: proveedorId, fuente_mensaje_original: texto }, reemplazarId);
        publicadas++;
      }
      toast(`${publicadas} ruta(s) publicada(s) ✅`);
      setFilas([]);
      setTexto('');
    } catch (err) {
      toast('Error al publicar: ' + (err.message || 'desconocido'), 'error');
    }
    setPublicando(false);
  };

  return (
    <div>
      <p className="section-label">PROVEEDOR</p>
      <select value={proveedorId} onChange={(e) => setProveedorId(e.target.value)} style={{
        background: 'var(--navy-card)', border: '1px solid var(--navy-border)', color: 'var(--white)',
        borderRadius: 10, padding: '13px 16px', width: '100%', fontSize: 15, marginBottom: 16,
      }}>
        {proveedores.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
      </select>

      <p className="section-label">MENSAJE DEL PROVEEDOR</p>
      <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={8} placeholder="Pega aquí el mensaje completo..." style={{
        background: 'var(--navy-card)', border: '1px solid var(--navy-border)', color: 'var(--white)',
        borderRadius: 10, padding: '13px 16px', width: '100%', fontSize: 14, marginBottom: 14, resize: 'vertical',
      }} />

      <button className="btn-primary" onClick={handleProcesar} disabled={procesando} style={{ marginBottom: 24 }}>
        {procesando ? <span className="spinner" /> : '🤖 Procesar mensaje'}
      </button>

      {filas.length > 0 && (
        <>
          <p className="section-label">REVISA Y EDITA ANTES DE PUBLICAR</p>
          {filas.map((f) => (
            <FilaEditable key={f._id} fila={f} proveedor={proveedor} proveedorId={proveedorId} onChange={(n) => updateFila(f._id, n)} onRemove={() => removeFila(f._id)} />
          ))}
          <button className="btn-primary" onClick={handlePublicar} disabled={publicando || hayFilasSinResolver} style={{ marginTop: 8 }}>
            {publicando ? <span className="spinner" /> : hayFilasSinResolver ? 'Revisando rutas existentes...' : `📢 Publicar ${filas.length} ruta(s)`}
          </button>
        </>
      )}
    </div>
  );
}
